import React from "react";
import { BlockMath, InlineMath } from "react-katex";
import EquationInput from "../../../../components/inputs/EquationInput";
import OpenTaskScoringHint from "../../../../components/ui/OpenTaskScoringHint";
import { selectExtendedVariant } from "../../../extendedVariantSelection";

import {
  absLinearLatex,
  containsAllNumbers,
  frac,
  fracLatex,
  fracRaw,
  MathLine,
  rand,
  updatePart,
  userRaw,
} from "./absoluteValueHelpers";

export const type = "MaturaAbsoluteValueEquationOpenTask";

const OPTIONS = [4, 5, 6, 7, 8].flatMap((k) =>
  Array.from({ length: 8 }, (_, i) => ({ k, a: i + 1 }))
).filter(({ k, a }) => k !== 4 || a !== 2);
export function getVariantCount() { return OPTIONS.length; }

function makeTask(params = rand(OPTIONS)) {
  const { k, a } = params;
  const leftSolution = frac((k - 3) * a, k + 1);
  const rightSolution = frac((k + 3) * a, k - 1);
  return {
    type,
    points: 4,
    k,
    a,
    variantIndex: params.variantIndex,
    breakpoints: [frac(-a), frac(a)],
    solutions: [leftSolution, rightSolution].sort((x, y) => x.num / x.den - y.num / y.den),
  };
}

export function generateTask(options = {}) {
  const original = options.variant === "original";
  return { ...makeTask(original ? { k: 4, a: 2 } : selectExtendedVariant(OPTIONS, options)),
    source: { variant: original ? "original" : "random", exam: "MATURA", session: "2024_GRU",
      taskNumber: "6", basedOn: "MMAP-R0-100-2412-6" } };
}

export function renderTask(task) {
  const { k, a } = task;
  return (
    <div>
      <p><strong>Zadanie maturalne - otwarte (0-4 pkt).</strong></p>
      <p>Rozwiąż równanie</p>
      <BlockMath
        math={`${absLinearLatex(k, -k * a)}+${absLinearLatex(1, -a)}=${absLinearLatex(-1, a)}+${absLinearLatex(1, a)}+${2 * a}`}
      />
      <p><strong>Zapisz obliczenia.</strong></p>
    </div>
  );
}

export function renderInput(task, userAnswer = {}, onChange, disabled) {
  const set = (key) => (value) => onChange(updatePart(userAnswer, key, value));
  return (
    <div style={{ display: "grid", gap: 18 }}>
      <OpenTaskScoringHint variant="independent" />
      <div>
        <p><strong>Krok 1 (1 pkt).</strong> Uprość równanie, korzystając z własności wartości bezwzględnej.</p>
        <EquationInput
          value={userAnswer.reduced || ""}
          onChange={set("reduced")}
          disabled={disabled}
          placeholder={`np. ${task.k}|x-${task.a}|=|x+${task.a}|+${2 * task.a}`}
          showPreview={false}
        />
      </div>
      <div>
        <p><strong>Krok 2 (1 pkt).</strong> Zapisz punkty podziału osi i przedziały przypadków.</p>
        <EquationInput
          value={userAnswer.cases || ""}
          onChange={set("cases")}
          disabled={disabled}
          placeholder={`np. -${task.a}, ${task.a} oraz (-∞,-${task.a}), [-${task.a},${task.a}), [${task.a},∞)`}
          showPreview={false}
        />
      </div>
      <div>
        <p><strong>Krok 3 (1 pkt).</strong> Rozwiąż równania w odpowiednich przypadkach.</p>
        <EquationInput
          value={userAnswer.caseSolutions || ""}
          onChange={set("caseSolutions")}
          disabled={disabled}
          placeholder={`np. x=${fracRaw(task.solutions[0])}, x=${fracRaw(task.solutions[1])}`}
          showPreview={false}
        />
      </div>
      <div>
        <p><strong>Krok 4 (1 pkt).</strong> Podaj końcowy zbiór rozwiązań.</p>
        <EquationInput
          value={userAnswer.final || ""}
          onChange={set("final")}
          disabled={disabled}
          placeholder={`np. ${fracRaw(task.solutions[0])}, ${fracRaw(task.solutions[1])}`}
          showPreview={false}
        />
      </div>
    </div>
  );
}

function reducedLooksRight(task, raw) {
  const s = String(raw || "").replace(/\s+/g, "");
  if (!s) return false;
  const hasMainAbs = s.includes(`|x-${task.a}|`) || s.includes(`abs(x-${task.a})`);
  const hasOtherAbs = s.includes(`|x+${task.a}|`) || s.includes(`abs(x+${task.a})`);
  const hasCoeff = s.includes(`${task.k}|x-${task.a}|`) || s.includes(`${task.k}*|x-${task.a}|`) || s.includes(`${task.k}abs(x-${task.a})`);
  return hasMainAbs && hasOtherAbs && hasCoeff && !s.includes(`|${task.a}-x|`);
}

export function checkAnswer(task, userAnswer = {}) {
  const steps = {
    reduced: reducedLooksRight(task, userRaw(userAnswer.reduced)),
    cases: containsAllNumbers(userRaw(userAnswer.cases), task.breakpoints),
    caseSolutions: containsAllNumbers(userRaw(userAnswer.caseSolutions), task.solutions),
    final: containsAllNumbers(userRaw(userAnswer.final), task.solutions),
  };
  const score = Object.values(steps).filter(Boolean).length;
  return { correct: score === 4, score, maxScore: 4, details: steps };
}

export function renderAnswer(task) {
  return (
    <div>
      <p><strong>Krok 1:</strong> <InlineMath math={`${task.k}${absLinearLatex(1, -task.a)}=${absLinearLatex(1, task.a)}+${2 * task.a}`} />.</p>
      <p><strong>Krok 2:</strong> punkty podziału: <InlineMath math={`${fracLatex(task.breakpoints[0])}`} /> oraz <InlineMath math={`${fracLatex(task.breakpoints[1])}`} />.</p>
      <p><strong>Krok 3:</strong> z przypadków otrzymujemy <InlineMath math={`x=${fracLatex(task.solutions[0])}`} /> oraz <InlineMath math={`x=${fracLatex(task.solutions[1])}`} />.</p>
      <p><strong>Krok 4:</strong> <InlineMath math={`x\\in\\left\\{${task.solutions.map(fracLatex).join(",\\;")}\\right\\}`} />.</p>
    </div>
  );
}

export function renderUserAnswer(userAnswer = {}) {
  return (
    <div>
      {userAnswer.reduced && <p>Uproszczenie: {userRaw(userAnswer.reduced)}</p>}
      {userAnswer.cases && <p>Przypadki: {userRaw(userAnswer.cases)}</p>}
      {userAnswer.caseSolutions && <p>Rozwiązania przypadków: {userRaw(userAnswer.caseSolutions)}</p>}
      {userAnswer.final && <p>Odpowiedź: {userRaw(userAnswer.final)}</p>}
    </div>
  );
}

export function renderExplanation(task) {
  const { k, a } = task;
  return (
    <div>
      <p><strong>Krok 1.</strong> Korzystamy z własności <InlineMath math={`|${a}-x|=|x-${a}|`} /> oraz <InlineMath math={`|${k}x-${k * a}|=${k}|x-${a}|`} />.</p>
      <MathLine math={`${k}|x-${a}|+|x-${a}|=|x-${a}|+|x+${a}|+${2 * a}`} />
      <MathLine math={`${k}|x-${a}|=|x+${a}|+${2 * a}`} />
      <p><strong>Krok 2.</strong> Punkty podziału osi to <InlineMath math={`-${a}`} /> oraz <InlineMath math={`${a}`} />.</p>
      <p><strong>Krok 3.</strong> Rozwiązujemy równanie w przedziałach.</p>
      <MathLine math={`- ${a}\\le x<${a}:\\quad ${k}(${a}-x)=x+${a}+${2 * a}\\quad\\Rightarrow\\quad x=${fracLatex(frac((k - 3) * a, k + 1))}`} />
      <MathLine math={`x\\ge ${a}:\\quad ${k}(x-${a})=x+${a}+${2 * a}\\quad\\Rightarrow\\quad x=${fracLatex(frac((k + 3) * a, k - 1))}`} />
      <p>W lewym przedziale otrzymujemy wartość spoza rozpatrywanego przedziału, więc jej nie dopisujemy.</p>
      <p><strong>Krok 4.</strong> Odpowiedź:</p>
      <MathLine math={`x\\in\\left\\{${task.solutions.map(fracLatex).join(",\\;")}\\right\\}`} />
    </div>
  );
}
